import React, { createContext, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";

import callEventStream from "service/realtime/CallEventStream";

// ZY-10312 — ported from expert-dashboard-frontend
// (commit eb4637750fdc, src/service/realtime/CallEventsContext.js). Shape kept
// identical so both consumers stay one pattern.

// Safety net. If a hangup never reaches us — dropped stream, backgrounded tab,
// SparkTG simply not sending it — the floater must not stay up forever.
const CALL_TTL_MS = 30 * 60 * 1000;

const CallEventsContext = createContext({ ongoingCall: null });

/**
 * Holds the doctor's currently ongoing SparkTG call.
 *
 * Context rather than an EventEmitter: emitters have no current value, so
 * anything mounting after the call started would never learn about it. phr
 * remounts entire pages on navigation; the context survives.
 */
export function CallEventsProvider({ children }) {
  const [ongoingCall, setOngoingCall] = useState(null);

  useEffect(() => {
    const applyCall = (call) => {
      if (!call) return;

      if (call.state === "ended") {
        // Only clear the call we are actually showing. Two calls can overlap,
        // and a late hangup for an older one must not dismiss a newer floater.
        setOngoingCall((current) =>
          current && current.callId === call.callId ? null : current,
        );
        return;
      }

      setOngoingCall({ ...call, receivedAt: Date.now() });
    };

    const unsubscribe = callEventStream.subscribe((event) => {
      if (event.type === "snapshot") {
        setOngoingCall(
          event.call ? { ...event.call, receivedAt: Date.now() } : null,
        );
        return;
      }
      applyCall(event.call);
    });

    callEventStream.start();

    return () => {
      unsubscribe();
      callEventStream.stop();
    };
  }, []);

  // Expire a stale call rather than leaving the floater pinned open. Driven by
  // receivedAt (our clock, when we saw it) and never the server timestamp —
  // clock skew would otherwise expire it instantly or never.
  useEffect(() => {
    if (!ongoingCall) return undefined;

    const elapsed = Date.now() - (ongoingCall.receivedAt || Date.now());
    const remaining = Math.max(0, CALL_TTL_MS - elapsed);
    const timer = setTimeout(() => setOngoingCall(null), remaining);

    return () => clearTimeout(timer);
  }, [ongoingCall]);

  return (
    <CallEventsContext.Provider value={{ ongoingCall }}>
      {children}
    </CallEventsContext.Provider>
  );
}

CallEventsProvider.propTypes = {
  children: PropTypes.node,
};

export function useOngoingCall() {
  return useContext(CallEventsContext).ongoingCall;
}

export default CallEventsContext;
